import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { isEmpty, debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import { RightsAccess } from '../../../Components/helper';
import { RP_IDENTIFIER_TAB_KEY } from './RangePlanTables/ProductIdentifierV2/product-identifier-v2.constants';

let breaks = [];
let timerId = 0;
function AllInformationTabs(props) {
  const reducer = useSelector((reducerstate) => reducerstate);
  const { t } = useTranslation('common');
  const {
    Users: { logInRights },
    UserDetails: { isCompanyAdminLoggedIn },
  } = reducer;
  const { CheckModulePermission } = useComponent();
  const { TestingRight, productCostingRight } = RightsAccess();
  const { activeTheTab, masterActiveTab, setSectionData, loader2, loader, MenuList } = props;
  const navRef = useRef(null);
  const btnRef = useRef(null);
  const vlinksRef = useRef(null);
  const hlinksRef = useRef(null);
  const [activeUnderline, setActiveUnderline] = useState({});
  const updateNav = () => {
    timerId = setTimeout(() => {
      const nav = navRef.current;
      const btn = btnRef.current;
      const vlinks = vlinksRef.current;
      const hlinks = hlinksRef.current;
      if (nav && btn && vlinks && hlinks) {
        const availableSpace = btn.classList.contains('hidden') ? nav.clientWidth : nav.clientWidth - btn.clientWidth - 30;

        if (vlinks.clientWidth > availableSpace) {
          if (vlinks.lastChild) {
            breaks.push(vlinks.clientWidth);
            hlinks.prepend(vlinks.lastChild);
          }

          if (btn.classList.contains('hidden')) {
            btn.classList.remove('hidden');
          }
        } else {
          if (availableSpace > breaks[breaks.length - 1] && !isEmpty(hlinks) && !isEmpty(vlinks) && hlinks.lastChild) {
            vlinks.append(hlinks.firstChild);
            breaks.pop();
          }
          if (breaks.length < 1) {
            btn.classList.add('hidden');
            hlinks.classList.add('hidden');
          }
        }
        btn.setAttribute('count', breaks.length);

        if (vlinks.clientWidth > availableSpace) {
          updateNav();
        }
      }
    }, 300);
  };

  const obj = {
    'bom-info': 'bom',
    specification: 'measurement',
    productTesting: 'testing',
    'care-label-info': 'care',
    'care-label-document': 'care',
    samples: 'samples',
    'product-costing': 'costing',
  };

  /**
   * Resolves whether the logged-in user may see a rights-controlled tab.
   * Keys must stay in sync with the tab event keys declared in ProductListNew, otherwise
   * the products module lookup resolves to undefined and every user is granted access.
   * Testing and Costing additionally require the company-level module right, matching the
   * development order navigation.
   */
  const modalCheck = useCallback(
    (name) => {
      if (name === 'productTesting') {
        return TestingRight && CheckModulePermission(logInRights, 'products', obj[name], isCompanyAdminLoggedIn);
      }
      if (name === 'product-costing') {
        return productCostingRight && CheckModulePermission(logInRights, 'products', obj[name], isCompanyAdminLoggedIn);
      }
      return CheckModulePermission(logInRights, 'products', obj[name], isCompanyAdminLoggedIn);
    },
    [TestingRight, productCostingRight, CheckModulePermission, logInRights, obj, isCompanyAdminLoggedIn],
  );

  useEffect(() => {
    const updateUnderline = () => {
      if (vlinksRef.current && masterActiveTab && MenuList?.length > 0) {
        const activeTab = vlinksRef.current.querySelector('.active');

        if (activeTab) {
          const tabRect = activeTab.getBoundingClientRect();
          const parentRect = vlinksRef.current.getBoundingClientRect();
          setActiveUnderline({
            width: tabRect.width,
            left: tabRect.left - parentRect.left,
          });
        } else {
          setActiveUnderline({
            width: 0,
            display: 'none',
          });
        }
      }
    };

    const debouncedUpdate = debounce(updateUnderline, 500);

    updateUnderline(); // Call initially

    window.addEventListener('resize', debouncedUpdate);

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      debouncedUpdate.cancel(); // Ensure cleanup
    };
  }, [masterActiveTab, MenuList]);

  /**
   * Tabs that are not listed here carry no user-rights module of their own and stay visible.
   * Product Identifier is the one Range Plan tab with a Range Plan right of its own, read live so
   * switching it off hides the tab without a reload.
   */
  const checkModalAccess = useCallback(
    (name) => {
      if (name === RP_IDENTIFIER_TAB_KEY) {
        return CheckModulePermission(logInRights, 'rangePlan', 'productIdentifier', isCompanyAdminLoggedIn);
      }
      return ['bom-info', 'specification', 'productTesting', 'care-label-info', 'care-label-document', 'samples', 'product-costing'].includes(name)
        ? modalCheck(name)
        : true;
    },
    [modalCheck, CheckModulePermission, logInRights, isCompanyAdminLoggedIn],
  );

  const toggleHidden = () => {
    const hlinks = hlinksRef.current;
    if (hlinks) {
      hlinks.classList.toggle('hidden');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (!loader && !loader2) {
        updateNav();
      }
    };

    const handleWindowResize = () => {
      clearTimeout(timerId);
      timerId = setTimeout(handleResize, 300);
    };

    if (!loader && !loader2) {
      updateNav();
      window.addEventListener('resize', handleWindowResize);
    }

    return () => {
      clearTimeout(timerId);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [MenuList.length, loader, loader2]);

  useEffect(() => {
    return () => {
      breaks = [];
    };
  }, []);

  return (
    Array.isArray(MenuList) &&
    MenuList.length > 0 &&
    !loader &&
    !loader2 && (
      <Container fluid className="p-0 mb-2">
        <div className="new-tab-conatiner">
          <div className="new-tab-list">
            <div className="tab-container">
              <nav className="greedy-nav" ref={navRef}>
                <ul className="subnav-ul visible-links" ref={vlinksRef}>
                  {MenuList?.length > 0
                    ? MenuList.map((element, index) => {
                        return (
                          checkModalAccess(element?.eventKey) && (
                            <li key={index}>
                              <button
                                type="button"
                                className={masterActiveTab === element.eventKey ? 'active' : ''}
                                // disabled={element?.isDisabled || isAPILoading}
                                onClick={() => {
                                  if (element?._id && element?.subType === 'customsection') {
                                    setSectionData(element, element.index);
                                    activeTheTab(element?.eventKey);
                                  } else {
                                    activeTheTab(element?.eventKey);
                                  }
                                }}
                              >
                                <div className="tab-item">
                                  <span className="d-flex text-bold ml-0 align-items-center">
                                    {element?.subType === 'customsection' ? element?.eventName : t(`${element?.eventName}`)}
                                  </span>
                                </div>
                              </button>
                            </li>
                          )
                        );
                      })
                    : null}
                </ul>
                <button type="button" className="btn-more-dropdown" onClick={toggleHidden} ref={btnRef}>
                  <span className="icon-More f-32 text-black" />
                </button>
                <ul className="hidden-links hidden" ref={hlinksRef} />
                <div className="tab-underline" style={activeUnderline} />
              </nav>
            </div>
          </div>
        </div>
      </Container>
    )
  );
}

export default AllInformationTabs;
